// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { BuiltInSortingFn } from "@tanstack/react-table";
import assertNever from "assert-never";
import { add, nextDay } from "date-fns";
import { isEmpty, mapValues, pick, pickBy } from "lodash";
import simplur from "simplur";

import { OpportunityType } from "~datatypes";

import { OpportunityTableColumnId } from "../../../../core/OpportunityCaseloadView/HydratedOpportunityPersonList";
import {
  allFeatureVariants,
  FeatureVariant,
} from "../../../../RootStore/types";
import UserStore from "../../../../RootStore/UserStore";
import { PartialRecord } from "../../../../utils/typeUtils";
import {
  DenialInputSettings,
  Opportunity,
  OpportunityRequirement,
  OpportunityTab,
  OpportunityTabGroups,
} from "../../types";
import { IApiOpportunityConfiguration } from "../interfaces";
import { OpportunityConfiguration } from "../interfaces/OpportunityConfiguration";
import { tabHistogram } from "./utils";

export function formatEligibilityText(dynamicText: string, count: number) {
  return simplur(
    ["", " " + dynamicText],
    [count, (c: number) => (c === 0 ? "Some" : c)],
  );
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export function hydrateSnooze(
  snooze: IApiOpportunityConfiguration["snooze"],
): OpportunityConfiguration["snooze"] {
  if (snooze && snooze.autoSnoozeParams) {
    const { type, params } = snooze.autoSnoozeParams;
    return {
      autoSnoozeParams: (snoozedOn: Date) => {
        switch (type) {
          case "snoozeDays":
            return add(snoozedOn, {
              days: params.days,
            });
          case "snoozeUntil":
            return nextDay(snoozedOn, WEEKDAYS.indexOf(params.weekday) as Day);
          default:
            assertNever(type);
        }
      },
    };
  } else {
    return snooze;
  }
}

export class ApiOpportunityConfiguration implements OpportunityConfiguration {
  constructor(
    protected configurationObject: IApiOpportunityConfiguration,
    protected userStore: UserStore,
  ) {}

  /**
   * Declared so the getter below can read it. Subclasses supply it.
   *
   * `declare` rather than `abstract`: this class is instantiated directly as
   * the fallback for every type with no custom config, and most types have no
   * custom count, so there is nothing for a subclass to be obliged to
   * implement.
   */
  declare countFromTabCounts?: (
    tabCounts: PartialRecord<OpportunityTab, number>,
  ) => number;

  get countByFunction():
    | ((opportunities: Opportunity[]) => number)
    | undefined {
    const countFromTabCounts = this.countFromTabCounts;
    if (!countFromTabCounts) return undefined;
    return (opportunities: Opportunity[]) =>
      countFromTabCounts(tabHistogram(opportunities));
  }

  get systemType() {
    return this.configurationObject.systemType;
  }
  get stateCode() {
    return this.configurationObject.stateCode;
  }
  get urlSection() {
    return this.configurationObject.urlSection;
  }
  get featureVariant() {
    const { featureVariant } = this.configurationObject;
    if (featureVariant && featureVariant in allFeatureVariants) {
      return featureVariant as FeatureVariant;
    }
    return undefined;
  }
  get inverseFeatureVariant() {
    const { inverseFeatureVariant } = this.configurationObject;
    if (inverseFeatureVariant && inverseFeatureVariant in allFeatureVariants) {
      return inverseFeatureVariant as FeatureVariant;
    }
    return undefined;
  }
  get label() {
    return this.configurationObject.displayName;
  }
  get firestoreCollection() {
    return this.configurationObject.firestoreCollection;
  }
  get snooze() {
    return hydrateSnooze(this.configurationObject.snooze);
  }

  get priority() {
    return this.configurationObject.priority;
  }

  get initialHeader() {
    return this.configurationObject.initialHeader;
  }
  get callToAction() {
    return this.configurationObject.callToAction;
  }
  get subheading() {
    return this.configurationObject.subheading;
  }
  get notifications() {
    return this.configurationObject.notifications;
  }

  eligibilityTextForCount = (count: number) =>
    formatEligibilityText(
      this.configurationObject.dynamicEligibilityText,
      count,
    );

  get denialButtonText() {
    return this.configurationObject.denialText;
  }

  get eligibilityDateText() {
    return this.configurationObject.eligibilityDateText;
  }
  eligibilityDateTextForTab(tab?: OpportunityTab): string | undefined {
    return this.eligibilityDateText;
  }

  get hideDenialRevert() {
    return this.configurationObject.hideDenialRevert;
  }

  get tabGroups(): OpportunityTabGroups {
    const tabs = this.configurationObject.tabGroups;
    // If we receive an empty object for tab groups, don't pass it through
    if (tabs && !isEmpty(tabs)) return tabs;
    return {
      "ELIGIBILITY STATUS": this.defaultEligibilityStatusTabs(),
    };
  }

  protected defaultEligibilityStatusTabs(): ReadonlyArray<OpportunityTab> {
    const almostEligibleTabTitle: OpportunityTab = "Almost Eligible";

    return [
      "Eligible Now",
      ...(this.supportsAlmostEligible ? [almostEligibleTabTitle] : []),
      ...(this.supportsSupervisorReview ? [this.supervisorReviewTabTitle] : []),
      ...(this.supportsSupervisorReviewOnGrants
        ? [this.grantApprovedTabTitle]
        : []),
      ...(this.supportsSubmitted ? [this.submittedTabTitle] : []),
      ...(this.supportsDenial ? [this.deniedTabTitle] : []),
    ];
  }

  get methodologyUrl() {
    return this.configurationObject.methodologyUrl;
  }

  get denialReasons() {
    return this.configurationObject.denialReasons;
  }

  /**
   * Returns the denial reasons map for indefinite reasons (you can set indefinite
   * reasons by overriding maxSnoozeDaysByDenialReason on a custom Opportunity Config class)
   */
  get indefiniteDenialReasons() {
    if (!isEmpty(this.maxSnoozeDaysByDenialReason)) {
      const indefiniteSnoozeSettings = pickBy(
        this.maxSnoozeDaysByDenialReason,
        (maxSnoozeDays) => maxSnoozeDays === undefined,
      );
      return pick(this.denialReasons, Object.keys(indefiniteSnoozeSettings));
    }
    return {};
  }

  get sidebarComponents() {
    return this.configurationObject.sidebarComponents;
  }

  get isAlert() {
    return this.configurationObject.isAlert;
  }

  get tooltipEligibilityText() {
    return this.configurationObject.tooltipEligibilityText;
  }

  get skipFormPreview() {
    return this.configurationObject.skipFormPreview;
  }

  get eligibleCriteriaCopy() {
    return this.configurationObject.eligibleCriteriaCopy;
  }

  get ineligibleCriteriaCopy() {
    return this.configurationObject.ineligibleCriteriaCopy;
  }

  get strictlyIneligibleCriteriaCopy() {
    return this.configurationObject.strictlyIneligibleCriteriaCopy;
  }

  get nonOmsCriteria(): OpportunityRequirement[] {
    return this.configurationObject.nonOmsCriteria;
  }

  get compareBy() {
    return this.configurationObject.compareBy;
  }

  get homepagePosition() {
    return this.configurationObject.homepagePosition;
  }

  get isEnabled(): boolean {
    const { activeFeatureVariants } = this.userStore;
    const { featureVariant, inverseFeatureVariant } = this;

    const featureVariantEnabled =
      !featureVariant || !!activeFeatureVariants[featureVariant];
    const inverseFeatureVariantDisabled =
      !!inverseFeatureVariant && !!activeFeatureVariants[inverseFeatureVariant];

    return featureVariantEnabled && !inverseFeatureVariantDisabled;
  }

  get deniedTabTitle(): OpportunityTab {
    if (this.configurationObject.deniedTabTitle)
      return this.configurationObject.deniedTabTitle as OpportunityTab;

    return this.isAlert ? "Overridden" : "Marked Ineligible";
  }

  get denialAdjective(): string {
    return (
      this.configurationObject.denialAdjective ??
      (this.isAlert ? "Overridden" : "Ineligible")
    );
  }

  get denialNoun(): string {
    return this.configurationObject.denialNoun ?? this.isAlert
      ? "Override Status"
      : "Ineligibility";
  }

  get submittedTabTitle(): OpportunityTab {
    return (this.configurationObject.submittedTabTitle ??
      "Pending") as OpportunityTab;
  }

  get omsCriteriaHeader(): string {
    if (this.configurationObject.omsCriteriaHeader)
      return this.configurationObject.omsCriteriaHeader;

    switch (this.stateCode) {
      case "US_MI":
        return "Validated by data from COMS";
      default:
        return "Requirements validated by OMS data";
    }
  }

  get nonOmsCriteriaHeader() {
    return (
      this.configurationObject.nonOmsCriteriaHeader ?? "Requirements to check"
    );
  }

  get emptyTabCopy() {
    return this.configurationObject.emptyTabCopy;
  }

  get tabPrefaceCopy() {
    return this.configurationObject.tabPrefaceCopy;
  }

  get supportsDenial() {
    return Object.keys(this.denialReasons).length > 0;
  }

  get supportsAlmostEligible() {
    return Object.keys(this.ineligibleCriteriaCopy).length > 0;
  }

  get supportsSubmitted() {
    return this.configurationObject.supportsSubmitted;
  }

  get supportsIneligible() {
    return this.configurationObject.supportsIneligible;
  }

  get showIneligibleFormButtons() {
    return this.configurationObject.showIneligibleFormButtons;
  }

  get hideIneligibleStatusPillOnProfile() {
    return this.configurationObject.hideIneligibleStatusPillOnProfile;
  }

  // This field controls if the opportunity manager will hydrate an opportunity
  // record when we instantiate a JII instance even if the record says
  // the person is ineligible.
  //
  // NOTE: Be wary when enabling this as it can vastly increase the number of
  // requests made when a user first loads up a searchable (officer, facility, etc.)
  get hydrateIneligibleRecordsInOpportunityManager() {
    return false;
  }

  // The columns explicitly named by the API config
  get apiEnabledColumnIds(): Array<OpportunityTableColumnId> {
    return this.configurationObject.enabledColumns
      ? (Object.keys(
          this.configurationObject.enabledColumns,
        ) as Array<OpportunityTableColumnId>)
      : [];
  }

  // The full column info provided by the API config
  get apiEnabledColumnInfo(): Partial<
    Record<
      OpportunityTableColumnId,
      {
        columnHeader: string | undefined;
        cellValue: string | undefined;
        sortingFn: BuiltInSortingFn | undefined;
      }
    >
  > {
    return this.configurationObject.enabledColumns ?? {};
  }

  /**
   * Table view columns enabled for this opportunity. If columns are not yet
   * configured in the admin panel, we go with a default set of columns
   * (defined here) in a default order (managed by the table presenter).
   * TODO(OBT-39481): Remove columnId default logic in base and
   * custom configs once all opportunities have configured columns
   * via the admin panel.
   */
  get enabledColumns(): Array<OpportunityTableColumnId> {
    if (this.apiEnabledColumnIds.length) return this.apiEnabledColumnIds;

    const defaultColumnIds: Array<OpportunityTableColumnId> = [
      "PERSON_NAME",
      "PERSON_DISPLAY_ID",
      "STATUS",
      "LAST_VIEWED",
      // In base to support generalizing this column, per TODO(#7453)
      "INSTANCE_DETAILS",
      "ASSIGNED_STAFF_NAME",
      "ALMOST_ELIGIBLE_STATUS",
      "ELIGIBILITY_DATE",
      "SNOOZE_ENDS_IN",
      "SUBMITTED_FOR",
      "CTA_BUTTON",
    ];
    if (this.systemType === "INCARCERATION") {
      defaultColumnIds.push("RELEASE_DATE");
      if (this.stateCode === "US_MI") defaultColumnIds.push("US_MI_UNIT_ID");
    }
    if (this.systemType === "SUPERVISION") {
      defaultColumnIds.push("SUPERVISION_EXPIRATION_DATE");
      if (this.stateCode === "US_NE") defaultColumnIds.push("US_NE_PEDD_DATE");
    }

    return defaultColumnIds;
  }

  get highlightCasesOnHomepage() {
    return this.configurationObject.highlightCasesOnHomepage;
  }

  get overdueOpportunityCalloutCopy() {
    return this.configurationObject.overdueOpportunityCalloutCopy ?? "overdue";
  }

  get highlightedCaseCtaCopy(): string {
    if (this.configurationObject.highlightedCaseCtaCopy)
      return this.configurationObject.highlightedCaseCtaCopy;
    // note: this error only triggers if highlightCasesOnHomepage is true but
    // highlightedCaseCtaCopy is not set, as it is not requested otherwise
    throw new Error(`Implement highlightedCaseCtaCopy() for ${this.label}`);
  }

  // TODO(#6450): Stop coalescing once the recidiviz-data config change deploys
  get zeroGrantsTooltip() {
    return (
      this.configurationObject.zeroGrantsTooltip ??
      "This officer has not granted any clients this opportunity in the past 12 months."
    );
  }

  /** Compound logic used to determine whether or not approval flows tabs render. */
  get supportsSupervisorReview() {
    return (
      this.supportsSupervisorReviewOnGrants ||
      this.supportsSupervisorReviewOnSnooze
    );
  }

  /** True when the grant approval flow is enabled for this opportunity. */
  get supportsSupervisorReviewOnGrants() {
    return this.configurationObject.supportsSupervisorReviewOnGrants;
  }

  /** True when the snooze approval flow is enabled for this opportunity.  */
  get supportsSupervisorReviewOnSnooze() {
    return this.configurationObject.supportsSupervisorReviewOnSnooze;
  }

  /**  Denial reasons that require a supervisor approval before triggering a snooze. */
  get reasonsRequiringApproval(): string[] {
    return this.configurationObject.reasonsRequiringApproval ?? [];
  }

  /** The tab title for opportunities pending review. */
  get supervisorReviewTabTitle() {
    return (this.configurationObject.supervisorReviewTabTitle ??
      "Supervisor Review") as OpportunityTab;
  }

  get awaitingRevisionsTabTitle(): OpportunityTab {
    return (this.configurationObject.awaitingRevisionsTabTitle ??
      "Awaiting Revisions") as OpportunityTab;
  }

  /** The tab title for opportunities a reviewer previously reviewed but is no longer the current reviewer for. */
  get historicalReviewTabTitle(): OpportunityTab {
    return (this.configurationObject.historicalReviewTabTitle ??
      "Past Reviews") as OpportunityTab;
  }

  /** The tab title for opportunity grant requests that have been approved. */
  get grantApprovedTabTitle() {
    return (this.configurationObject.grantApprovedTabTitle ??
      "Approved by Supervisor") as OpportunityTab;
  }

  /** The status message shown on an opportunity record after review approval. */
  get grantApprovedStatusMessage() {
    return (
      this.configurationObject.grantApprovedStatusMessage ??
      "Approved by Supervisor"
    );
  }

  /** The dropdown option label that submits an opportunity grant for review. */
  get grantReviewDropdownLabel() {
    return (
      this.configurationObject.grantReviewDropdownLabel ??
      "Submit for Supervisor Approval"
    );
  }

  /** The status message shown on an opportunity record while a snooze is pending review approval. */
  get snoozeReviewStatusMessage() {
    return this.configurationObject.snoozeReviewStatusMessage ?? "Under Review";
  }

  /** The status message shown on an opportunity record while a grant is pending review approval. */
  get grantReviewStatusMessage() {
    return this.configurationObject.grantReviewStatusMessage ?? "Under Review";
  }

  /** When set, only users with this feature variant active are permitted to act as a reviewer for this opportunity's approval flows. */
  get reviewerFeatureVariant(): FeatureVariant | undefined {
    return this.configurationObject.reviewerFeatureVariant as
      | FeatureVariant
      | undefined;
  }

  get subcategoryHeadings() {
    return this.configurationObject.subcategoryHeadings;
  }

  get subcategoryOrderings() {
    return this.configurationObject.subcategoryOrderings;
  }

  get markSubmittedOptionsByTab() {
    return this.configurationObject.markSubmittedOptionsByTab;
  }

  get allSubcategoriesOfSubmitted(): string[] | undefined {
    return this.subcategoryOrderings?.[this.submittedTabTitle];
  }

  get markSubmittedOnFormDownload() {
    // If we require approval before granting an opportunity, we should not mark
    // submitted on form download.
    return (
      this.configurationObject.markSubmittedOnFormDownload ??
      !this.supportsSupervisorReviewOnGrants
    );
  }

  get snoozeCompanionOpportunityTypes(): OpportunityType[] {
    // TODO(#8022): Once this configuration is available via the admin panel, it should be appear here
    return (this.configurationObject.snoozeCompanionOpportunityTypes ??
      []) as OpportunityType[];
  }

  get caseNoteHeaders(): string[] {
    return (this.configurationObject.caseNoteHeaders ?? []) as string[];
  }

  get caseNotesTitle() {
    return this.configurationObject.caseNotesTitle ?? "Relevant Contact Notes";
  }

  /**
   * Used to enable the progressive loading feature within Table View to display an initial
   * batch size of rows with a button that allows the user to load more batches of rows.
   * Starts with a default of 30 rows, but can be overridden by the opportunity.
   *
   * NOTE: If an opportunity uses subcategories, each subcategory renders its own table and progressive loading
   * instance, meaning each subcategory will have its own "Load more" button and the batch size will be applied
   * to each subcategory's table.
   *
   * TODO(#8663): Discuss with product how to best handle progressive loading for opportunities with subcategories
   * and implement accordingly.
   */
  enableProgressiveLoading = false;
  progressiveLoadingBatchSize = 30;

  /**
   * The header/subheading used for the indefinite denial reasons section in the
   * opportunity denial view.
   */
  get indefiniteSnoozeSectionHeader() {
    return (
      this.configurationObject.indefiniteSnoozeSectionHeader ??
      "Indefinite Snooze"
    );
  }
  get indefiniteSnoozeSectionSubheading() {
    return (
      this.configurationObject.indefiniteSnoozeSectionHeader ??
      "An approval request will be sent to your supervisor before this client can be snoozed indefinitely and removed from this opportunity"
    );
  }

  /**
   * Determines whether we filter out indefinite snoozes from the "Marked Ineligible"
   * tab in the table view. By default, we will filter out, but this field can be
   * overridden for opportunities where the indefinitely snoozed clients should
   * still surface in table view.
   */
  get excludeIndefiniteSnoozesFromTableView() {
    return true;
  }

  /**
   * Returns a record of each denial reason to the relevant max snooze length.
   * Override to set individual denial reason max snoozes or map a reason to undefined
   * to mark the given reason as indefinite snooze.
   *
   * Empty record for auto snooze opps.
   */
  get maxSnoozeDaysByDenialReason(): Record<string, number | undefined> {
    const configuredMaxSnoozeDays = this.snooze?.maxSnoozeDays;
    if (configuredMaxSnoozeDays) {
      return mapValues(this.denialReasons, () => configuredMaxSnoozeDays);
    }
    return {};
  }

  /**
   * Used to configure which denial reasons require text input from the user.
   */
  get denialInputSettings(): Record<string, DenialInputSettings> {
    return {};
  }

  get customSubmittedText(): string | undefined {
    return undefined;
  }

  get enableWorkflowsFilter(): boolean {
    return this.configurationObject.enableWorkflowsFilter ?? false;
  }

  get hidePreviewModal(): boolean {
    return false;
  }

  get hideCtaButton(): boolean {
    return false;
  }

  get enableSupervisorReviewChain(): boolean {
    return false;
  }

  get hideWorkflowsTabGroupSelector(): boolean {
    return false;
  }
}
